import * as React from "react"
import { ReactNode } from "react"

import { cn } from "@/lib/utils"
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { Search } from "lucide-react";

export interface InputProps extends React.ComponentProps<"input"> {
  label?: ReactNode
  labelClassName?: string
  icon?: typeof Search
  requiredIndicator?: ReactNode
}

function Input({ label, labelClassName, className, type, icon: Icon, requiredIndicator, value, ...props }: InputProps) {
  return (
    <label className={cn('w-full flex flex-col justify-center gap-1 text-sm font-normal leading-5 text-gray-700 relative', labelClassName)}>
      <div className="flex items-center">
        {label}
        {requiredIndicator && <span className="text-red-500 ml-1">{requiredIndicator}</span>}
      </div>
      <input
        type={type}
        data-slot="input"
        className={cn(
          "w-full border-[0.5px] border-neutral-200 rounded-md py-2 px-3 font-normal text-base text-gray-950 shadow-xs",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          'disabled:bg-neutral-100 disabled:border-none',
          !!Icon && 'pl-10',
          className
        )}
        value={value || ''}
        {...props}
      />
      {Icon && <Icon className="absolute left-3 w-5 h-5 text-neutral-500" />}
    </label>
  )
}

export interface FormInputProps<T extends FieldValues> extends InputProps {
  name: FieldPath<T>
  control: Control<T>
  containerClassName?: string
}

export const FormInput = <T extends FieldValues>({ name, control, containerClassName, ...props }: FormInputProps<T>) =>
  <FormField
    name={name}
    control={control}
    render={({ field }) =>
      <FormItem className={cn('gap-0', containerClassName)}>
        <FormControl>
          <Input
            {...field}
            {...props}
            onChange={e => {
              field.onChange(e.target.value)
              props.onChange?.(e)
            }}
          />
        </FormControl>
        <FormMessage />
      </FormItem>}
  />

export { Input }
